<template>
  <div class="bc-page">
    <Card>
      <div class="toolbar">
        <Button type="primary" @click="openCreate">新建权益卡</Button>
        <Button class="m-l-10" @click="load">刷新</Button>
        <span class="hint">客人登录后扫描领取码即可持卡，结算按卡折扣自动计价</span>
      </div>
      <Table class="m-t-16" :loading="loading" :columns="columns" :data="list" border stripe />
    </Card>

    <Modal
      v-model="showForm"
      :title="form._id ? '编辑权益卡' : '新建权益卡'"
      width="780"
      :mask-closable="false"
      @on-ok="saveForm"
      @on-cancel="resetForm"
    >
      <Form :label-width="120">
        <FormItem label="名称">
          <Input v-model="form.name" placeholder="如：XX 团队卡" />
        </FormItem>
        <FormItem label="折扣">
          <InputNumber v-model="form.discount_zhe" :min="0.1" :max="9.9" :step="0.1" style="width: 160px" />
          <span class="hint">如 8 表示八折（实付原价的 80%）</span>
        </FormItem>
        <FormItem label="开始时间">
          <DatePicker v-model="form.valid_from" type="datetime" placeholder="开始" style="width: 240px" />
        </FormItem>
        <FormItem label="结束时间">
          <DatePicker v-model="form.valid_to" type="datetime" placeholder="结束" style="width: 240px" />
        </FormItem>
        <FormItem label="状态">
          <Select v-model="form.status" style="width: 200px">
            <Option value="on">启用</Option>
            <Option value="off">停用</Option>
          </Select>
        </FormItem>
        <FormItem label="参与商品">
          <Select v-model="form.food_scope" style="width: 200px">
            <Option value="all">全部商品</Option>
            <Option value="include">指定商品</Option>
          </Select>
        </FormItem>
        <FormItem v-if="form.food_scope === 'include'" label="已选商品">
          <div class="food-picked">
            <Tag
              v-for="id in form.food_ids"
              :key="id"
              closable
              @on-close="removeFoodId(id)"
              class="food-tag"
            >
              {{ foodNameOf(id) }}
            </Tag>
            <span v-if="!form.food_ids.length" class="hint-inline">尚未选择商品</span>
          </div>
          <div class="m-t-8">
            <Button type="dashed" icon="ios-list" @click="openFoodPicker">从商品列表勾选</Button>
            <span class="hint">已选 {{ form.food_ids.length }} 个</span>
          </div>
        </FormItem>
        <FormItem label="领取说明">
          <Input v-model="form.notice" type="textarea" :rows="2" placeholder="展示给客人，如：仅团队用餐当日有效" />
        </FormItem>
        <FormItem label="内部备注">
          <Input v-model="form.remark" type="textarea" :rows="2" />
        </FormItem>
        <FormItem v-if="!form._id" label="小程序版本">
          <Select v-model="form.env_version" style="width: 200px">
            <Option value="release">正式版 release</Option>
            <Option value="trial">体验版 trial</Option>
            <Option value="develop">开发版 develop</Option>
          </Select>
          <span class="hint">写入领取码跳转页</span>
        </FormItem>
      </Form>
    </Modal>

    <Modal
      v-model="showFoodPicker"
      title="选择参与折扣的商品"
      width="760"
      :mask-closable="false"
      @on-ok="confirmFoodPick"
      @on-cancel="closeFoodPicker"
    >
      <div class="food-picker-toolbar">
        <Input
          v-model="foodPickerKey"
          search
          enter-button="搜索"
          placeholder="商品名称"
          style="width: 280px"
          @on-search="searchFoodPicker"
        />
        <span class="hint">勾选后点确定；可多选</span>
      </div>
      <Table
        class="m-t-12"
        ref="foodPickerTable"
        :loading="foodPickerLoading"
        :columns="foodPickerColumns"
        :data="foodPickerList"
        border
        height="380"
        @on-selection-change="onFoodPickerSelection"
      />
      <Page
        class="m-t-12"
        :total="foodPickerTotal"
        :current="foodPickerPage"
        :page-size="foodPickerPageSize"
        show-total
        @on-change="onFoodPickerPage"
      />
    </Modal>

    <Modal v-model="showQr" title="领取码" width="420" :footer-hide="true">
      <div v-if="qrCard" class="qr-box">
        <div class="qr-name">{{ qrCard.name }}</div>
        <div class="qr-zhe">{{ zheLabel(qrCard.pay_percent) }}</div>
        <img v-if="qrImage" :src="qrImage" alt="领取码" class="qr-img" />
        <div class="qr-scene">{{ qrScene }}</div>
        <div v-if="qrLinkText" class="qr-link">{{ qrLinkText }}</div>
        <div v-if="qrLinkOutdated" class="qr-warn">链接仍是旧版，请点「重新生成」后再下载</div>
        <div class="qr-actions">
          <Button type="primary" :loading="qrLoading" @click="regenQr">重新生成</Button>
          <Button v-if="qrLinkText" class="m-l-10" @click="copyQrLink">复制链接</Button>
          <a v-if="qrImage" class="m-l-10" :href="qrImage" target="_blank" rel="noopener">预览</a>
          <a v-if="qrImage" class="m-l-10" :href="qrImage" :download="qrScene + '.png'">下载</a>
        </div>
        <Select v-model="qrEnv" class="m-t-12" style="width: 200px">
          <Option value="release">正式版 release</Option>
          <Option value="trial">体验版 trial</Option>
          <Option value="develop">开发版 develop</Option>
        </Select>
        <p class="hint-block">请点「下载」保存新 PNG 后再扫；不要扫弹窗里旧图（浏览器可能缓存）。可「复制链接」发到微信里打开测试。</p>
      </div>
    </Modal>

    <Modal v-model="showClaims" :title="claimsTitle" width="760" :footer-hide="true">
      <Table :loading="claimsLoading" :columns="claimColumns" :data="claims" border stripe size="small" />
      <Page
        class="m-t-12"
        :total="claimsTotal"
        :current="claimsPage"
        :page-size="20"
        show-total
        @on-change="loadClaims"
      />
    </Modal>
  </div>
</template>

<script>
import {
  benefitCardList,
  benefitCardCreate,
  benefitCardUpdate,
  benefitCardDelete,
  benefitCardQrcode,
  benefitCardClaims,
} from '@/api/benefitCard'
import { goodsPage } from '@/api/goods'

function emptyForm() {
  return {
    _id: '',
    name: '',
    discount_zhe: 8,
    valid_from: null,
    valid_to: null,
    status: 'on',
    food_scope: 'all',
    food_ids: [],
    notice: '',
    remark: '',
    env_version: 'trial',
  }
}

function foodIdOf(row) {
  if (!row) return ''
  return row._id != null ? String(row._id) : ''
}

function payPercentToZhe(p) {
  const n = Number(p)
  if (!Number.isFinite(n) || n <= 0) return 8
  return Math.round(n) / 10
}

export default {
  name: 'BenefitCards',
  data() {
    return {
      loading: false,
      list: [],
      showForm: false,
      form: emptyForm(),
      foodNameMap: {},
      showFoodPicker: false,
      foodPickerLoading: false,
      foodPickerList: [],
      foodPickerTotal: 0,
      foodPickerPage: 1,
      foodPickerPageSize: 10,
      foodPickerKey: '',
      foodPickerSelectedIds: [],
      foodPickerColumns: [
        { type: 'selection', width: 56, align: 'center' },
        {
          title: '商品',
          key: 'name',
          minWidth: 160,
          render: (h, { row }) => h('span', row.name || row.food_name || '—'),
        },
        {
          title: '价格',
          key: 'price',
          width: 100,
          render: (h, { row }) => h('span', row.price != null ? `￥${row.price}` : '—'),
        },
        {
          title: '状态',
          key: 'status',
          width: 80,
          render: (h, { row }) =>
            h('span', !row.status || row.status === 'up' ? '上架' : '下架'),
        },
      ],
      showQr: false,
      qrCard: null,
      qrEnv: 'trial',
      qrLoading: false,
      qrCacheBust: 0,
      showClaims: false,
      claims: [],
      claimsTotal: 0,
      claimsPage: 1,
      claimsCardId: '',
      claimsLoading: false,
      claimColumns: [
        {
          title: '昵称',
          key: 'nickName',
          minWidth: 120,
          render: (h, { row }) => h('span', row.nickName || '—'),
        },
        { title: '手机号', key: 'phone', width: 140, render: (h, { row }) => h('span', row.phone || '—') },
        {
          title: '领取时间',
          key: 'claimed_at',
          width: 180,
          render: (h, { row }) => h('span', this.formatTime(row.claimed_at)),
        },
      ],
      columns: [],
    }
  },
  computed: {
    qrImage() {
      if (!this.qrCard || !this.qrCard.qrcode_url) return ''
      const base = this.imageFullUrl(this.qrCard.qrcode_url)
      const v =
        this.qrCacheBust ||
        (this.qrCard.updated_at ? new Date(this.qrCard.updated_at).getTime() : '') ||
        Date.now()
      return `${base}${base.includes('?') ? '&' : '?'}v=${v}`
    },
    qrScene() {
      const code = this.qrCard && this.qrCard.claim_code
      return code ? `c${code}` : ''
    },
    qrLinkText() {
      const raw = this.qrCard && this.qrCard.qr_content
      return raw ? String(raw).trim() : ''
    },
    qrLinkOutdated() {
      const link = this.qrLinkText
      return link && !link.includes('/api/benefit')
    },
    claimsTitle() {
      return this.qrCard && this.qrCard.name ? `领取记录 · ${this.qrCard.name}` : '领取记录'
    },
  },
  created() {
    this.columns = [
      { title: '名称', key: 'name', minWidth: 100 },
      {
        title: '折扣',
        key: 'pay_percent',
        minWidth: 100,
        render: (h, { row }) => h('span', this.zheLabel(row.pay_percent)),
      },
      {
        title: '有效期',
        key: 'valid',
        width: 330,
        render: (h, { row }) =>
          h('span', `${this.formatTime(row.valid_from)} ~ ${this.formatTime(row.valid_to)}`),
      },
      {
        title: '商品',
        key: 'food_scope',
        minWidth: 100,
        render: (h, { row }) =>
          h('span', row.food_scope === 'include' ? `指定 ${((row.food_ids || []).length)}` : '全部'),
      },
      {
        title: '状态',
        key: 'status',
        minWidth: 100,
        render: (h, { row }) => h('span', row.status === 'on' ? '启用' : '停用'),
      },
      {
        title: '已领',
        key: 'claim_count',
        minWidth: 100,
        render: (h, { row }) => h('span', String(row.claim_count || 0)),
      },
      {
        title: '操作',
        key: 'action',
        width: 340,
        render: (h, { row }) =>
          h('div', { style: { whiteSpace: 'nowrap' } }, [
            h(
              'Button',
              { props: { type: 'primary', size: 'small' }, on: { click: () => this.openEdit(row) } },
              '编辑'
            ),
            h(
              'Button',
              {
                props: { size: 'small' },
                style: { marginLeft: '8px' },
                on: { click: () => this.openQr(row) },
              },
              '领取码'
            ),
            h(
              'Button',
              {
                props: { size: 'small' },
                style: { marginLeft: '8px' },
                on: { click: () => this.openClaims(row) },
              },
              '领取记录'
            ),
            h(
              'Poptip',
              {
                props: {
                  confirm: true,
                  transfer: true,
                  title:
                    Number(row.claim_count) > 0
                      ? `已有 ${row.claim_count} 人领取，确认删除该权益卡吗？`
                      : '确认删除该权益卡吗？',
                },
                on: { 'on-ok': () => this.removeCard(row) },
              },
              [
                h(
                  'Button',
                  { props: { type: 'error', size: 'small' }, style: { marginLeft: '8px' } },
                  '删除'
                ),
              ]
            ),
          ]),
      },
    ]
  },
  mounted() {
    this.load()
  },
  methods: {
    zheLabel(p) {
      const n = Number(p)
      if (!Number.isFinite(n) || n <= 0) return '—'
      const z = n / 10
      const text = Number.isInteger(z) ? String(z) : String(Math.round(z * 10) / 10)
      return `${text}折`
    },
    formatTime(t) {
      if (!t) return '—'
      const d = new Date(t)
      if (Number.isNaN(d.getTime())) return '—'
      const p = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
    },
    imageFullUrl(path) {
      if (!path) return ''
      if (/^https?:\/\//i.test(path)) return path
      const apiBase = (process.env.VUE_APP_BASEURL || '').replace(/\/$/, '')
      const origin = apiBase.replace(/\/api$/i, '')
      const p = path.startsWith('/') ? path : `/${path}`
      return `${origin}${p}`
    },
    foodNameOf(id) {
      const sid = String(id)
      return this.foodNameMap[sid] || sid.slice(0, 8) + '…'
    },
    async load() {
      this.loading = true
      try {
        const res = await benefitCardList()
        if (res.data && res.data.code === 200) {
          this.list = res.data.data || []
        } else {
          this.$Message.error((res.data && res.data.msg) || '加载失败')
        }
      } catch (e) {
        this.$Message.error(e.message || '加载失败')
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.form = emptyForm()
      this.foodNameMap = {}
      this.showForm = true
    },
    openEdit(row) {
      const food_ids = Array.isArray(row.food_ids)
        ? row.food_ids.map((x) => String(x)).filter(Boolean)
        : []
      this.form = {
        _id: row._id,
        name: row.name || '',
        discount_zhe: payPercentToZhe(row.pay_percent),
        valid_from: row.valid_from ? new Date(row.valid_from) : null,
        valid_to: row.valid_to ? new Date(row.valid_to) : null,
        status: row.status === 'off' ? 'off' : 'on',
        food_scope: row.food_scope === 'include' ? 'include' : 'all',
        food_ids,
        notice: row.notice || '',
        remark: row.remark || '',
        env_version: row.qr_env_version || 'trial',
      }
      this.showForm = true
      if (food_ids.length) this.resolveFoodNames(food_ids)
    },
    async resolveFoodNames(ids) {
      const need = (ids || []).map(String).filter((id) => id && !this.foodNameMap[id])
      if (!need.length) return
      try {
        const res = await goodsPage({ pageNo: 1, pageSize: 500 })
        if (res.data && res.data.code === 200 && Array.isArray(res.data.data)) {
          const map = { ...this.foodNameMap }
          res.data.data.forEach((row) => {
            const id = foodIdOf(row)
            if (id) map[id] = row.name || row.food_name || id
          })
          this.foodNameMap = map
        }
      } catch (_) {}
    },
    removeFoodId(id) {
      const sid = String(id)
      this.form.food_ids = (this.form.food_ids || []).filter((x) => String(x) !== sid)
    },
    openFoodPicker() {
      this.foodPickerSelectedIds = [...(this.form.food_ids || [])]
      this.foodPickerPage = 1
      this.foodPickerKey = ''
      this.showFoodPicker = true
      this.loadFoodPicker()
    },
    closeFoodPicker() {
      this.showFoodPicker = false
    },
    searchFoodPicker() {
      this.foodPickerPage = 1
      this.loadFoodPicker()
    },
    onFoodPickerPage(p) {
      this.foodPickerPage = p
      this.loadFoodPicker()
    },
    onFoodPickerSelection(rows) {
      const pageIds = (this.foodPickerList || []).map(foodIdOf).filter(Boolean)
      const selectedOnPage = new Set((rows || []).map(foodIdOf).filter(Boolean))
      const kept = (this.foodPickerSelectedIds || []).filter((id) => !pageIds.includes(String(id)))
      this.foodPickerSelectedIds = kept.concat([...selectedOnPage])
    },
    async loadFoodPicker() {
      this.foodPickerLoading = true
      try {
        const res = await goodsPage({
          pageNo: this.foodPickerPage,
          pageSize: this.foodPickerPageSize,
          key: this.foodPickerKey || undefined,
        })
        if (res.data && res.data.code === 200) {
          const selected = new Set((this.foodPickerSelectedIds || []).map(String))
          const rows = res.data.data || []
          this.foodPickerList = rows.map((row) => ({
            ...row,
            _checked: selected.has(foodIdOf(row)),
          }))
          this.foodPickerTotal = Number(res.data.total) || 0
          const map = { ...this.foodNameMap }
          this.foodPickerList.forEach((row) => {
            const id = foodIdOf(row)
            if (id) map[id] = row.name || row.food_name || id
          })
          this.foodNameMap = map
        } else {
          this.$Message.error((res.data && res.data.msg) || '加载商品失败')
        }
      } catch (e) {
        this.$Message.error(e.message || '加载商品失败')
      } finally {
        this.foodPickerLoading = false
      }
    },
    confirmFoodPick() {
      this.form.food_ids = Array.from(
        new Set((this.foodPickerSelectedIds || []).map(String).filter(Boolean))
      )
      this.showFoodPicker = false
    },
    resetForm() {
      this.form = emptyForm()
      this.foodNameMap = {}
    },
    buildPayload() {
      const food_ids =
        this.form.food_scope === 'include'
          ? (this.form.food_ids || []).map(String).filter(Boolean)
          : []
      return {
        name: this.form.name,
        discount_zhe: this.form.discount_zhe,
        valid_from: this.form.valid_from || null,
        valid_to: this.form.valid_to || null,
        status: this.form.status,
        food_scope: this.form.food_scope,
        food_ids,
        notice: this.form.notice,
        remark: this.form.remark,
        env_version: this.form.env_version,
      }
    },
    async saveForm() {
      if (!this.form.name || !String(this.form.name).trim()) {
        this.$Message.warning('请填写权益卡名称')
        return false
      }
      if (this.form.food_scope === 'include' && !(this.form.food_ids && this.form.food_ids.length)) {
        this.$Message.warning('请勾选至少一个参与折扣的商品')
        return false
      }
      const payload = this.buildPayload()
      try {
        let res
        if (this.form._id) {
          res = await benefitCardUpdate(this.form._id, payload)
        } else {
          res = await benefitCardCreate(payload)
        }
        if (res.data && res.data.code === 200) {
          this.$Message.success('保存成功')
          this.showForm = false
          this.load()
        } else {
          this.$Message.error((res.data && res.data.msg) || '保存失败')
          return false
        }
      } catch (e) {
        this.$Message.error(e.message || '保存失败')
        return false
      }
    },
    async openQr(row) {
      this.qrCard = { ...row }
      this.qrEnv = row.qr_env_version || 'trial'
      this.qrCacheBust = Date.now()
      this.showQr = true
      const content = row.qr_content ? String(row.qr_content) : ''
      const needsRegen = !row.qrcode_url || !content.includes('/api/benefit')
      if (needsRegen) {
        this.$Message.warning('领取码链接已升级，正在重新生成…')
        await this.regenQr()
      }
    },
    copyQrLink() {
      const link = this.qrLinkText
      if (!link) {
        this.$Message.warning('请先生成领取码')
        return
      }
      const done = () => this.$Message.success('链接已复制，可发到微信里打开测试')
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(link).then(done).catch(() => {
          this.$Message.info(link)
        })
        return
      }
      this.$Message.info(link)
    },
    async regenQr() {
      if (!this.qrCard || !this.qrCard._id) return
      this.qrLoading = true
      try {
        const res = await benefitCardQrcode(this.qrCard._id, { env_version: this.qrEnv })
        if (res.data && res.data.code === 200 && res.data.data) {
          this.qrCard = res.data.data.card || { ...this.qrCard, ...res.data.data }
          this.qrCacheBust = Date.now()
          this.$Message.success('领取码已生成')
          this.load()
        } else {
          this.$Message.error((res.data && res.data.msg) || '生成失败')
        }
      } catch (e) {
        this.$Message.error(e.message || '生成失败')
      } finally {
        this.qrLoading = false
      }
    },
    openClaims(row) {
      this.qrCard = { ...row }
      this.claimsCardId = row._id
      this.showClaims = true
      this.loadClaims(1)
    },
    async loadClaims(page = 1) {
      if (!this.claimsCardId) return
      this.claimsPage = page
      this.claimsLoading = true
      try {
        const res = await benefitCardClaims(this.claimsCardId, { page, pageSize: 20 })
        if (res.data && res.data.code === 200 && res.data.data) {
          this.claims = res.data.data.list || []
          this.claimsTotal = res.data.data.total || 0
        }
      } catch (e) {
        this.$Message.error(e.message || '加载失败')
      } finally {
        this.claimsLoading = false
      }
    },
    async removeCard(row) {
      if (!row || !row._id) return
      try {
        const res = await benefitCardDelete(row._id)
        if (res.data && res.data.code === 200) {
          this.$Message.success('删除成功')
          if (this.qrCard && String(this.qrCard._id) === String(row._id)) {
            this.showQr = false
            this.qrCard = null
          }
          if (String(this.claimsCardId) === String(row._id)) {
            this.showClaims = false
            this.claimsCardId = ''
          }
          this.load()
        } else {
          this.$Message.error((res.data && res.data.msg) || '删除失败')
        }
      } catch (e) {
        this.$Message.error(e.message || '删除失败')
      }
    },
  },
}
</script>

<style scoped>
.toolbar { display: flex; align-items: center; flex-wrap: wrap; }
.m-l-10 { margin-left: 10px; }
.m-t-8 { margin-top: 8px; }
.m-t-12 { margin-top: 12px; }
.m-t-16 { margin-top: 16px; }
.hint { margin-left: 8px; color: #999; font-size: 12px; }
.hint-inline { color: #999; font-size: 12px; }
.hint-block { margin-top: 12px; color: #999; font-size: 12px; line-height: 1.5; }
.food-picked { min-height: 32px; }
.food-tag { margin: 0 8px 8px 0; }
.food-picker-toolbar { display: flex; align-items: center; justify-content: space-between; }
.qr-box { text-align: center; padding: 8px 0 16px; }
.qr-name { font-size: 16px; font-weight: 600; }
.qr-zhe { margin-top: 4px; color: #e87822; font-weight: 600; }
.qr-img { width: 220px; height: 220px; margin: 16px 0; object-fit: contain; }
.qr-scene { font-size: 12px; color: #999; }
.qr-link { margin-top: 8px; font-size: 11px; color: #666; word-break: break-all; line-height: 1.4; padding: 0 8px; }
.qr-warn { margin-top: 8px; font-size: 12px; color: #ed4014; padding: 0 8px; }
.qr-actions { margin-top: 12px; }
</style>
